import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { Image02Icon, Notification01Icon, TimeScheduleIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Input,
  InputGroup,
  PhoneNumberPicker,
  Select,
  ToggleSwitchInput,
} from 'components/FormComponents';

export const CandidateInformation = forwardRef(
  (
    { candidate, company, brand, teams, companyUsers, onUpdate = () => {}, hideBrand = false },
    ref,
  ) => {
    const { t } = useTranslation();
    const { register, reset, control, handleSubmit } = useForm({
      defaultValues: {
        ...candidate,
        contact_number: candidate?.contact_number ?? '',
      },
    });

    const team_id = useWatch({ control, name: 'team_id' });
    const user_id = useWatch({ control, name: 'user_id' });

    const [ccNotificationEmailsEnabled, setCcNotificationEmailsEnabled] = useState(
      !!candidate.cc_notification_emails,
    );

    const handleUpdate = async data => {
      return onUpdate({
        ...data,
        contact_number: data.contact_number?.trim() || null,
        cc_notification_emails: ccNotificationEmailsEnabled ? data?.cc_notification_emails : '',
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        submit: () => handleSubmit(handleUpdate)(),
      }),
      [handleSubmit, handleUpdate, ccNotificationEmailsEnabled],
    );

    useEffect(() => {
      if (!candidate) return;
      reset({
        ...candidate,
        contact_number: candidate.contact_number ?? '',
      });
    }, [candidate]);

    useEffect(() => {
      setCcNotificationEmailsEnabled(!!candidate.cc_notification_emails);
    }, [candidate?.cc_notification_emails]);

    return (
      <form
        className="d-flex flex-column u-width-100 u-gap-medium p-8"
        onSubmit={handleSubmit(handleUpdate)}>
        <div className="flex flex-row gap-8 flex-wrap">
          <div
            className={cn(
              'card',
              'card-with-border',
              'u-padding',
              'w-full sm:w-1/3 md:w-full lg:w-1/3',
            )}>
            <div className="inline">
              <InputGroup title={t('common.firstName')}>
                <Input
                  name="first_name"
                  register={register}
                  validators={{ required: true }}
                  placeholder={t('check.candidateInformation.firstNamePlaceholder')}
                />
              </InputGroup>
              <InputGroup title={t('common.lastName')}>
                <Input
                  name="last_name"
                  register={register}
                  validators={{ required: true }}
                  placeholder={t('check.candidateInformation.lastNamePlaceholder')}
                />
              </InputGroup>
            </div>
            <InputGroup title={t('common.email')}>
              <Input
                name="email"
                register={register}
                validators={{ required: true }}
                placeholder={t('check.candidateInformation.emailPlaceholder')}
              />
            </InputGroup>
            <InputGroup title={t('common.phoneNumber')}>
              <Controller
                control={control}
                name="contact_number"
                render={({ field: { onChange, value } }) => (
                  <PhoneNumberPicker
                    value={value ?? ''}
                    onChange={onChange}
                    defaultCountry={company?.country}
                  />
                )}
              />
            </InputGroup>
            <div className="inline">
              <InputGroup title={t('common.jobRole')}>
                <Input
                  name="job_role"
                  register={register}
                  placeholder={t('check.candidateInformation.jobTitlePlaceholder')}
                />
              </InputGroup>
              <InputGroup title={t('common.department')}>
                <Input
                  name="department"
                  register={register}
                  placeholder={t('check.candidateInformation.departmentPlaceholder')}
                />
              </InputGroup>
            </div>
            <InputGroup title={t('check.infoFields.referenceCode')}>
              <Input
                name="reference_code"
                register={register}
                placeholder={t('check.candidateInformation.referenceCodePlaceholder')}
              />
            </InputGroup>

            {hideBrand && (
              <InputGroup title="Brand">
                <Input disabled={true} inputProps={{ value: brand?.name }} />
              </InputGroup>
            )}

            {teams.length > 1 && (
              <InputGroup title={t('common.team')} className="u-margin-bottom--large">
                <Select
                  name="team_id"
                  placeholder={t('common.team')}
                  register={register}
                  value={team_id}
                  useDefault>
                  {teams.map(team => (
                    <Select.Item key={team.id} value={team.id}>
                      {team.name}
                    </Select.Item>
                  ))}
                </Select>
              </InputGroup>
            )}

            <InputGroup title={t('common.document')}>
              <Input
                disabled={true}
                register={register}
                name="document_name"
                placeholder={t('check.candidateInformation.documentPlaceholder')}
              />
            </InputGroup>
          </div>
          <div className="flex flex-col gap-8 w-full sm:w-1/4 md:w-full lg:w-1/3">
            <div className={cn('card', 'card-with-border', 'u-padding')}>
              <div>
                <div className={cn('u-bold')}>
                  {t('check.candidateInformation.candidateAssignment')}
                </div>
                <InputGroup
                  title={t('check.candidateInformation.assignedUserDescription')}
                  className="u-margin-bottom--0">
                  <Select
                    name="user_id"
                    placeholder={t('common.user')}
                    register={register}
                    value={user_id}
                    useDefault
                    useSearch>
                    {companyUsers?.map(user => (
                      <Select.Item key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} • {user.email}
                      </Select.Item>
                    ))}
                  </Select>
                </InputGroup>
              </div>
            </div>

            <ToggleSwitchInput
              noMaxWidth
              text={t('check.candidateInformation.notificationList')}
              subtext={t('check.candidateInformation.notificationListSubtext')}
              onChange={e => setCcNotificationEmailsEnabled(e.target.checked)}
              icon={Notification01Icon}
              className="u-margin-bottom--0"
              value={ccNotificationEmailsEnabled}>
              <InputGroup title={t('check.candidateInformation.emails')}>
                <div className="d-flex flex-column u-width-100 u-gap-small">
                  <Input
                    name="cc_notification_emails"
                    placeholder={t('check.candidateInformation.ccEmailsPlaceholder')}
                    register={register}
                    disabled={!ccNotificationEmailsEnabled}
                  />
                  <span className="u-font-size-x-small secondary-text">
                    {t('check.candidateInformation.ccEmailsHint')}
                  </span>
                </div>
              </InputGroup>
            </ToggleSwitchInput>
            {!hideBrand && (
              <div className="bg-neutral-1 p-8 rounded-3xl border border-border-primary flex flex-col gap-8">
                <div className="p-8 border border-border-primary  rounded-3xl bg-neutral-2 ">
                  {brand?.brand_logo ? (
                    <div
                      className="bg-contain bg-center bg-no-repeat min-h-48"
                      style={
                        brand?.brand_logo
                          ? { backgroundImage: `url(${brand.brand_logo})` }
                          : undefined
                      }
                      role="img"
                      aria-label={t('check.candidateInformation.brandLogoAriaLabel')}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2">
                      <HugeiconsIcon
                        icon={Image02Icon}
                        size="40"
                        strokeWidth={1.5}
                        className="text-neutral-6"
                      />
                      <span className="text-neutral-6 text-2xl font-regular">
                        {t('check.candidateInformation.noLogoConfigured')}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col">
                    <span className="text-neutral-6 text-sm font-medium flex items-center gap-2 uppercase">
                      {t('common.brand')}
                    </span>
                    <span className="text-primary-4 text-3xl font-medium">{brand?.name}</span>
                  </div>

                  <div className="flex flex-wrap gap-8">
                    <span className="text-neutral-6 text-xl font-light flex items-center gap-2">
                      <span
                        className="inline-block w-6 h-6 shrink-0 rounded-full border border-border-primary"
                        style={{ backgroundColor: brand?.color_button || 'transparent' }}
                        aria-hidden
                      />
                      {t('check.candidateInformation.buttonColor')}
                    </span>
                    <span className="text-neutral-6 text-xl font-light flex items-center gap-2">
                      <span
                        className="inline-block w-6 h-6 shrink-0 rounded-full border border-border-primary"
                        style={{ backgroundColor: brand?.color_button_text || 'transparent' }}
                        aria-hidden
                      />
                      {t('check.candidateInformation.buttonTextColor')}
                    </span>
                    <span className="text-neutral-6 text-xl font-light flex items-center gap-2">
                      <span
                        className="inline-block w-6 h-6 shrink-0 rounded-full border border-border-primary"
                        style={{ backgroundColor: brand?.color_link || 'transparent' }}
                        aria-hidden
                      />
                      {t('check.candidateInformation.linkColor')}
                    </span>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-neutral-6 text-xl font-light leading-8 flex items-center gap-2">
                      <HugeiconsIcon
                        icon={Notification01Icon}
                        size="16"
                        strokeWidth={2}
                        className="text-green-2"
                      />
                      {t('check.candidateInformation.remindersConfigured', {
                        count: brand?.reminders,
                      })}
                    </span>

                    <span className="text-neutral-6 text-xl font-light leading-8 flex flex-wrap items-center gap-2">
                      <HugeiconsIcon icon={TimeScheduleIcon} size="16" strokeWidth={2} />
                      {t('check.candidateInformation.statusChangesTo')}
                      <span className="bg-red-1 py-1 px-2 rounded-full text-red-2 font-medium mx-1">
                        {t('common.overdue')}
                      </span>
                      {t('check.candidateInformation.afterReminder', {
                        number: brand?.overdue_after_reminder,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
    );
  },
);
CandidateInformation.displayName = 'CandidateInformation';
